import { JsonSchemaModel } from '../Models/JsonSchemaModel';

/** Expand alternatives for editing while retaining constraints shared by every branch. */
export function getSchemaAlternatives(schema: JsonSchemaModel): JsonSchemaModel[] {
	const { anyOf, oneOf, ...shared } = schema;
	const alternatives = oneOf ?? anyOf;
	if (alternatives)
		return alternatives.flatMap((branch) =>
			getSchemaAlternatives(branch).map((leaf) => ({
				...shared,
				...leaf,
				properties:
					shared.properties || leaf.properties
						? Object.fromEntries(
								Array.from(new Set([...Object.keys(shared.properties ?? {}), ...Object.keys(leaf.properties ?? {})])).map((name) => {
									const left = shared.properties?.[name];
									const right = leaf.properties?.[name];
									return [name, left && right ? { ...left, ...right, allOf: [left, right] } : (left ?? right)!];
								}),
							)
						: undefined,
				required: Array.from(new Set([...(shared.required ?? []), ...(leaf.required ?? [])])),
				// Presentation fields are merged above; the original intersection remains authoritative.
				allOf: [schema, leaf],
			})),
		);
	if (Array.isArray(schema.type)) return schema.type.map((type) => ({ ...schema, type }));
	return [schema];
}

export function schemaAllowsNull(schema: JsonSchemaModel): boolean {
	if (schema['x-allowsNull']) return true;
	return schemaMatchesValue(schema, null);
}

export function schemaMatchesValue(schema: JsonSchemaModel | boolean, value: unknown): boolean {
	if (typeof schema === 'boolean') return schema;
	if (schema.oneOf && schema.oneOf.filter((branch) => schemaMatchesValue(branch, value)).length !== 1) return false;
	if (schema.anyOf && !schema.anyOf.some((branch) => schemaMatchesValue(branch, value))) return false;
	if (schema.allOf && !schema.allOf.every((branch) => schemaMatchesValue(branch, value))) return false;
	if ('const' in schema && !jsonEquals(value, schema.const)) return false;
	if (schema.enum && !schema.enum.some((item) => jsonEquals(item, value))) return false;
	if (Array.isArray(schema.type)) return schema.type.some((type) => schemaMatchesValue({ ...schema, type }, value));
	// Applicator keywords constrain values even when no explicit type keyword is present.
	if (schema.type === undefined) {
		if (Array.isArray(value)) return schemaMatchesValue({ ...schema, type: 'array' }, value);
		if (value !== null && typeof value === 'object') return schemaMatchesValue({ ...schema, type: 'object' }, value);
	}
	switch (schema.type) {
		case 'null':
			return value === null;
		case 'string':
			return typeof value === 'string';
		case 'integer':
			return typeof value === 'number' && Number.isInteger(value);
		case 'number':
			return typeof value === 'number';
		case 'boolean':
			return typeof value === 'boolean';
		case 'array':
			return (
				Array.isArray(value) &&
				(schema.items === undefined || Array.isArray(schema.items) || value.every((item) => schemaMatchesValue(schema.items as JsonSchemaModel, item)))
			);
		case 'object':
			if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
			const object = value as Record<string, unknown>;
			if (schema.required?.some((name) => !Object.prototype.hasOwnProperty.call(object, name))) return false;
			return Object.entries(object).every(([name, item]) => {
				const property = schema.properties?.[name];
				if (property) return schemaMatchesValue(property, item);
				return (
					schema.additionalProperties !== false && (typeof schema.additionalProperties !== 'object' || schemaMatchesValue(schema.additionalProperties, item))
				);
			});
		default:
			return true;
	}
}

function jsonEquals(left: unknown, right: unknown): boolean {
	if (left === right) return true;
	if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') return false;
	if (Array.isArray(left) !== Array.isArray(right)) return false;
	const a = left as Record<string, unknown>;
	const b = right as Record<string, unknown>;
	return (
		Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((key) => Object.prototype.hasOwnProperty.call(b, key) && jsonEquals(a[key], b[key]))
	);
}

export function getNonNullSchema(schema: JsonSchemaModel): JsonSchemaModel {
	const alternatives = getSchemaAlternatives(schema).filter((branch) => branch.type !== 'null');
	return alternatives.length === 1 ? alternatives[0] : schema;
}

/** Apply collection transforms to the contract before deriving legacy display flags. */
export function transformCollectionSchema(schema: JsonSchemaModel, wrap?: boolean, unwrap?: boolean): JsonSchemaModel {
	const unwrapSchema = (value: JsonSchemaModel): JsonSchemaModel => {
		if (value.anyOf || value.oneOf) return { anyOf: (value.oneOf ?? value.anyOf)!.map(unwrapSchema) };
		if (Array.isArray(value.type)) return { anyOf: value.type.map((type) => unwrapSchema({ ...value, type })) };
		if (value.type === 'null') return value;
		return value.items && !Array.isArray(value.items) ? value.items : value.type === 'array' ? {} : value;
	};
	const result = unwrap ? unwrapSchema(schema) : schema;
	return wrap ? { type: 'array', items: result } : result;
}
